import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button as MuiButton,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import PageHeader1 from "../../components/common/PageHeader1";
import {
  fetchUserTickets,
  deleteUserTicket,
} from "../../Redux/Reducers/UserTicketReducer";
import socket from "../../socket/socket";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermissions } from "../../context/AuthContext";

function UserSupportList() {
  const { can } = usePermissions();
  const dispatch = useDispatch();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const pagination = useSelector(
    (state) => state.UserTicketReducer?.pagination || {}
  );
  const ticketData = useSelector(
    (state) => state.UserTicketReducer?.data || []
  );

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("open");
  const [isConnected, setIsConnected] = useState(socket.connected);

  useEffect(() => {
    const senderData = JSON.parse(localStorage.getItem("vedhvidhi-admin-data"));
    const senderId = senderData?._id;

    function onConnect() {
      setIsConnected(true);
      if (senderId) {
        socket.emit("new user", senderId);
      }
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, []);

  useEffect(() => {
    const handleNewTicket = () => {
      if (activeTab === "open") {
        toast.info("A new ticket has arrived! Refreshing list...");
        const { page, pageSize } = paginationModel;
        dispatch(
          fetchUserTickets({
            page: page + 1,
            limit: pageSize,
            status: "active",
            userType: "user",
          })
        );
      }
    };

    socket.on("new ticket", handleNewTicket);
    return () => socket.off("new ticket", handleNewTicket);
  }, [dispatch, activeTab, paginationModel]);

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    const status = activeTab === "open" ? "active" : "close";
    dispatch(
      fetchUserTickets({
        page: page + 1,
        limit: pageSize,
        status,
        userType: "user",
      })
    );
  }, [paginationModel, dispatch, activeTab]);

  const handleClickOpen = (ticketId) => {
    setSelectedTicketId(ticketId);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedTicketId(null);
  };

  const handleDelete = async () => {
    if (selectedTicketId) {
      await dispatch(deleteUserTicket(selectedTicketId));
      const { page, pageSize } = paginationModel;
      const status = activeTab === "open" ? "active" : "close";
      dispatch(
        fetchUserTickets({
          page: page + 1,
          limit: pageSize,
          status,
          userType: "user",
        })
      );
    }
    handleClose();
  };

  const filteredData =
    ticketData?.filter((ticket) =>
      activeTab === "open"
        ? ticket?.status === "active"
        : ticket?.status === "close"
    ) || [];

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "ticketId", headerName: "Ticket Number", width: 150 },
    { field: "userName", headerName: "User Name", width: 200 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "phoneNumber", headerName: "Phone Number", width: 150 },
    {
      field: "feedback",
      headerName: "Feedback",
      width: 250,
      renderCell: ({ row }) => (
        <span>
          {row.feedback.length > 25
            ? row.feedback.substring(0, 25) + "..."
            : row.feedback}
        </span>
      ),
    },
    { field: "createdAt", headerName: "Created At", width: 200 },
    { field: "updatedAt", headerName: "Updated At", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: ({ row }) => (
        <div>
          {can('user_tickets:reply') && (
            <Tooltip title="View/Reply to Ticket">
              <IconButton component={Link} to={`/user-chat/${row.id}`}>
                <i className="icofont-eye text-info"></i>
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={() => handleClickOpen(row.id)}>
            <i className="icofont-ui-delete text-danger"></i>
          </IconButton>
        </div>
      ),
    },
  ];

  const rows =
    filteredData.map((ticket, index) => ({
      id: ticket?._id || index.toString(),
      sono: paginationModel.page * paginationModel.pageSize + index + 1,
      userName: ticket?.fullName || "N/A",
      email: ticket?.email || "N/A",
      phoneNumber: ticket?.mobile || "N/A",
      feedback: ticket?.description || "No feedback provided",
      ticketId: ticket?.ticketId || "N/A",
      status: ticket?.status === "active" ? "Open" : "Closed",
      createdAt: ticket?.createdAt
        ? new Date(ticket?.createdAt).toLocaleString()
        : "",
      updatedAt: ticket?.updatedAt
        ? new Date(ticket?.updatedAt).toLocaleString()
        : "",
    })) || [];

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
      <div className="body d-flex py-1">
        <div className="container-xxl">
          <PageHeader1 pagetitle="User Ticket List" />
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Ticket Status Tabs"
            >
              <Tab label="Open Tickets" value="open" />
              <Tab label="Closed Tickets" value="close" />
            </Tabs>
          </Box>

          <div className="row g-0 mb-3">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <Box sx={{ width: "100%", height: 650 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      getRowId={(row) => row.id}
                      disableRowSelectionOnClick
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{ toolbar: { showQuickFilter: true } }}
                      paginationMode="server"
                      rowCount={pagination?.totalDocs || 0}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                    />
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this user support ticket?
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={handleClose}>
              Cancel
            </MuiButton>
            <MuiButton onClick={handleDelete} color="error">
              Delete
            </MuiButton>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}

export default UserSupportList;